import React, {useCallback, useEffect, useState} from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    IconButton, LinearProgress, MenuItem, Paper, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, Autocomplete,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LinkIcon from '@mui/icons-material/Link';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ApiService from '../network/API';
import ScheduleDays from '../components/ScheduleDays';

const ScheduleChart = React.lazy(() => import('../components/ScheduleChart'));

const SEMESTER_START = '2026-09-01';

// Конец периода по умолчанию — ближайший учебный день после сегодня (по Самаре): пн–чт и вс → завтра, пт → понедельник (+3), сб → понедельник (+2).
const defaultEnd = () => {
    const parts = new Intl.DateTimeFormat('en-CA', {timeZone: 'Europe/Samara', year: 'numeric', month: '2-digit', day: '2-digit'}).formatToParts(new Date());
    const get = (t) => Number(parts.find((p) => p.type === t).value);
    const base = new Date(get('year'), get('month') - 1, get('day'));
    const dow = base.getDay(); // 0 — вс, 5 — пт, 6 — сб
    base.setDate(base.getDate() + (dow === 5 ? 3 : dow === 6 ? 2 : 1));
    return `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-${String(base.getDate()).padStart(2, '0')}`;
};

const iso = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const addDays = (d, n) => {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
};

const dayTitle = (date) => {
    const [y, m, d] = date.split('-').map(Number);
    const s = new Date(y, m - 1, d).toLocaleDateString('ru-RU', {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'});
    return s.charAt(0).toUpperCase() + s.slice(1);
};

const roomText = (rooms) => rooms.map((r) => (r.number ? `ауд. ${r.number}${r.building ? ` (корп. ${r.building})` : ''}` : r.code)).filter(Boolean).join(', ');

// Расписание преподавателя из сервиса расписания колледжа: дни от новых к старым.
const AdminSchedulePage = () => {
    const today = new Date();
    // По умолчанию — с начала учебного года (1 сентября 2026) по ближайший учебный день (завтра; в пятницу и субботу — понедельник).
    const [from, setFrom] = useState(SEMESTER_START);
    const [to, setTo] = useState(defaultEnd());
    const [data, setData] = useState(null);
    // Чьё расписание смотрим: own — наше (со статистикой и графиком), teacher / room / group — поиск по любому преподавателю, кабинету или группе.
    const [kind, setKind] = useState('own');
    const [name, setName] = useState('');
    const [options, setOptions] = useState([]);
    const [searching, setSearching] = useState(false);
    const [inputQuery, setInputQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showEmpty, setShowEmpty] = useState(false);
    const [groupFilter, setGroupFilter] = useState('');
    const [order, setOrder] = useState('desc'); // desc — новые даты сверху (по умолчанию), asc — старые сверху
    // Окно настройки предмета: {row, tagId, hours, key, applyAll, tags|null}
    const [subjectDialog, setSubjectDialog] = useState(null);
    const [saving, setSaving] = useState(false);
    const [dialogError, setDialogError] = useState(null);

    const load = useCallback((f, t, k = 'own', n = '') => {
        setLoading(true);
        setError(null);
        ApiService.getSchedule(f, t, k === 'own' ? undefined : k, k === 'own' ? undefined : n)
            .then(setData)
            .catch((e) => setError((typeof e === 'string' ? e : e?.reason || e?.message) || 'Не удалось загрузить расписание'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        document.title = 'Расписание';
        load(from, to);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setRange = (f, t) => {
        setFrom(iso(f));
        setTo(iso(t));
        if (kind === 'own' || name) load(iso(f), iso(t), kind, name);
    };

    const todayStr = () => iso(new Date());

    // Переключение «Моё / Преподаватель / Кабинет / Группа».
    const changeKind = (next) => {
        if (!next || next === kind) return;
        setKind(next);
        setName('');
        setOptions([]);
        setGroupFilter('');
        setError(null);
        if (next === 'own') {
            setFrom(SEMESTER_START);
            setTo(defaultEnd());
            load(SEMESTER_START, defaultEnd(), 'own', '');
        } else {
            // Поиск «кто сегодня в кабинете» — по умолчанию только сегодня.
            setFrom(todayStr());
            setTo(todayStr());
            setData(null);
        }
    };

    // Открыть расписание выбранного преподавателя / кабинета / группы (из поиска или по ссылке в паре).
    const pick = (k, n) => {
        if (!n) return;
        const short = kind !== 'own';
        const f = short ? from : todayStr();
        const t = short ? to : todayStr();
        setKind(k);
        setName(n);
        setGroupFilter('');
        setFrom(f);
        setTo(t);
        load(f, t, k, n);
    };

    // Подсказки поиска с небольшой задержкой при вводе.
    useEffect(() => {
        if (kind === 'own') return undefined;
        const timer = setTimeout(() => {
            setSearching(true);
            ApiService.searchSchedule(kind, inputQuery)
                .then((list) => setOptions(Array.isArray(list) ? list : []))
                .catch(() => setOptions([]))
                .finally(() => setSearching(false));
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind, inputQuery]);

    // Код предмета из названия в расписании («МДК 01.01 Разработка…» → «МДК 01.01»), иначе всё название.
    const guessKey = (subject) => (subject.match(/^\s*\p{L}+\.?\s*\d[\d.]*\d/u) || [subject])[0].trim();

    const openEdit = (row) => {
        setDialogError(null);
        setSubjectDialog({row, tagId: row.tagId, hours: row.totalHours ?? '', key: row.scheduleKey || '', applyAll: false, tags: null, linking: false});
    };

    const openLink = (row) => {
        setDialogError(null);
        setSubjectDialog({row, tagId: '', hours: '', key: guessKey(row.scheduleSubject), applyAll: false, tags: [], linking: true});
        ApiService.getScheduleGroupSubjects(row.groupId)
            .then((tags) => setSubjectDialog((d) => (d ? {...d, tags} : d)))
            .catch(() => {});
    };

    const saveSubject = async () => {
        setSaving(true);
        setDialogError(null);
        try {
            await ApiService.updateScheduleSubject(subjectDialog.tagId, {
                scheduleKey: subjectDialog.key,
                applyToSameKey: false,
            });
            setSubjectDialog(null);
            load(from, to);
        } catch (e) {
            setDialogError((typeof e === 'string' ? e : e?.reason || e?.message) || 'Не удалось сохранить');
        } finally {
            setSaving(false);
        }
    };

    const todayIso = iso(new Date());
    // Группы, которые встречаются в расписании за период, — для фильтра.
    const groupOptions = Array.from(new Set((data?.days || []).flatMap((d) => d.lessons.flatMap((l) => l.groups)))).sort((a, b) => a.localeCompare(b, 'ru'));
    // Фильтр по группе: оставляем только пары выбранной группы; день без таких пар скрываем (если не включены «дни без пар»).
    const filteredDays = (data?.days || []).map((d) => (
        groupFilter ? {...d, lessons: d.lessons.filter((l) => l.groups.includes(groupFilter))} : d
    ));
    const visibleDays = filteredDays.filter((d) => (groupFilter
        ? d.lessons.length > 0 || (showEmpty && d.notes.length >= 0)
        : showEmpty || d.lessons.length > 0 || d.notes.length > 0));
    // Сервер отдаёт от новых к старым; при «по возрастанию» разворачиваем.
    const days = order === 'desc' ? visibleDays : [...visibleDays].reverse();
    // Статистика по выбранной группе.
    const stats = groupFilter && data?.stats ? data.stats.filter((r) => r.group === groupFilter) : data?.stats;
    const statsLessons = stats ? stats.reduce((n, r) => n + r.lessons, 0) : 0;
    const statsHours = stats ? stats.reduce((n, r) => n + r.hours, 0) : 0;

    return (
        <Container maxWidth="md" sx={{py: 4}}>
            <Typography variant="h4" fontWeight={700}>Расписание</Typography>
            {data && (
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    {kind === 'room' ? 'Кабинет ' : kind === 'group' ? 'Группа ' : ''}{data.teacher}
                    {data.updatedAt && ` · обновлено ${new Date(data.updatedAt).toLocaleString('ru-RU', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'})}`}
                </Typography>
            )}

            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{mb: 2}}>
                <ToggleButtonGroup size="small" exclusive color="primary" value={kind} onChange={(_, v) => changeKind(v)}>
                    <ToggleButton value="own">Моё</ToggleButton>
                    <ToggleButton value="teacher">Преподаватель</ToggleButton>
                    <ToggleButton value="room">Кабинет</ToggleButton>
                    <ToggleButton value="group">Группа</ToggleButton>
                </ToggleButtonGroup>
                {kind !== 'own' && (
                    <Autocomplete
                        size="small"
                        sx={{width: 300}}
                        options={options}
                        loading={searching}
                        value={name ? {value: name, label: name} : null}
                        isOptionEqualToValue={(a, b) => a.value === b.value}
                        getOptionLabel={(o) => o.label || ''}
                        filterOptions={(x) => x}
                        noOptionsText={inputQuery ? 'Ничего не найдено' : 'Начните вводить'}
                        onInputChange={(_, v, reason) => { if (reason === 'input') setInputQuery(v); }}
                        onChange={(_, v) => v && pick(kind, v.value)}
                        renderInput={(params) => (
                            <TextField {...params} label={kind === 'teacher' ? 'ФИО преподавателя' : kind === 'room' ? 'Кабинет (например 301/4)' : 'Группа'}/>
                        )}
                    />
                )}
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{mb: 2}}>
                <TextField size="small" type="date" label="С" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{shrink: true}}/>
                <TextField size="small" type="date" label="По" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{shrink: true}}/>
                <Button variant="contained" disabled={loading || !from || !to || (kind !== 'own' && !name)} onClick={() => load(from, to, kind, name)}>Показать</Button>
                <Button size="small" onClick={() => {
                    setFrom(SEMESTER_START);
                    setTo(defaultEnd());
                    if (kind === 'own' || name) load(SEMESTER_START, defaultEnd(), kind, name);
                }}>С 1 сентября</Button>
                <Button size="small" onClick={() => setRange(addDays(new Date(), -30), addDays(new Date(), 30))}>±30 дней</Button>
                <Button size="small" onClick={() => {
                    const n = new Date();
                    setRange(new Date(n.getFullYear(), n.getMonth(), 1), new Date(n.getFullYear(), n.getMonth() + 1, 0));
                }}>Этот месяц</Button>
                <Button size="small" onClick={() => setRange(new Date(), addDays(new Date(), 14))}>Ближайшие 2 недели</Button>
                <TextField select size="small" label="Группа" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} sx={{minWidth: 150}}>
                    <MenuItem value="">Все группы</MenuItem>
                    {groupOptions.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </TextField>
                <Button size="small" variant="outlined" onClick={() => setOrder((o) => (o === 'desc' ? 'asc' : 'desc'))}
                        startIcon={order === 'desc' ? <ArrowDownwardIcon/> : <ArrowUpwardIcon/>}>
                    {order === 'desc' ? 'Сначала новые' : 'Сначала старые'}
                </Button>
                <FormControlLabel control={<Switch checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)}/>} label="Дни без пар"/>
            </Stack>

            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

            {!loading && data && kind === 'own' && (
                <React.Suspense fallback={null}>
                    <ScheduleChart days={filteredDays} from={data.from} to={data.to}/>
                </React.Suspense>
            )}

            {!loading && data && kind === 'own' && stats && (
                <Paper variant="outlined" sx={{borderRadius: 2, mb: 2, p: 2}}>
                    <Typography variant="subtitle1" fontWeight={700}>Проведено часов по группам и предметам</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1}}>
                        {data.from} — {data.to}. Для предметов из журнала АСУ РСО «проведено» это часы пар, записанных в журнал, а план берётся из журнала (так же, как в АСУ РСО);
                        для остальных считаются закончившиеся пары расписания (прогнозные не учитываются), одна пара = 2 часа. Предмет из расписания связывается с предметом группы
                        в системе по ключу (код вроде «МДК 01.01» из названия предмета или заданный вручную).
                    </Typography>
                    {stats.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Проведённых пар в этом периоде нет.</Typography>
                    ) : (
                        <>
                            <Stack direction="row" spacing={1} sx={{mb: 2}}>
                                <Chip color="primary" label={`Всего: ${statsHours} ч`}/>
                                <Chip variant="outlined" label={`${statsLessons} пар`}/>
                            </Stack>
                            <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2}}>
                                {Object.entries(stats.reduce((acc, r) => {
                                    (acc[r.group] = acc[r.group] || []).push(r);
                                    return acc;
                                }, {})).map(([group, rows]) => (
                                    <Paper key={group} variant="outlined" sx={{p: 2, borderRadius: 2}}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 1.5}}>
                                            <Typography variant="h6" fontWeight={700}>{group}</Typography>
                                            <Chip size="small" label={`${rows.reduce((n, r) => n + r.hours, 0)} ч`}/>
                                        </Stack>
                                        <Stack spacing={2} divider={<Box sx={{borderTop: 1, borderColor: 'divider'}}/>}>
                                            {rows.map((r) => {
                                                const planned = r.totalHours != null && r.totalHours > 0;
                                                const left = planned ? r.totalHours - r.hours : null;
                                                const pct = planned ? Math.min(100, Math.round((r.hours / r.totalHours) * 100)) : 0;
                                                return (
                                                    <Box key={`${r.tagId ?? r.subject}`}>
                                                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                                            <Box sx={{minWidth: 0}}>
                                                                <Typography variant="body2" fontWeight={600} sx={{wordBreak: 'break-word'}}>{r.subject}</Typography>
                                                                {r.tagId == null && (
                                                                    <Tooltip title="В системе у этой группы нет предмета с таким ключом">
                                                                        <Chip size="small" variant="outlined" color="warning" label="не связан" sx={{mt: 0.5}}/>
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                            {r.tagId != null ? (
                                                                <Tooltip title="План часов и ключ предмета"><IconButton size="small" onClick={() => openEdit(r)}><EditOutlinedIcon fontSize="small"/></IconButton></Tooltip>
                                                            ) : r.groupId != null ? (
                                                                <Tooltip title="Связать с предметом группы"><IconButton size="small" onClick={() => openLink(r)}><LinkIcon fontSize="small"/></IconButton></Tooltip>
                                                            ) : null}
                                                        </Stack>
                                                        {r.source === 'JOURNAL' && r.unrecordedHours > 0 && (
                                                            <Tooltip title="Пары по расписанию прошли, а записей в журнале АСУ РСО для них нет — эти часы не учтены в «проведено»">
                                                                <Chip size="small" color="warning" variant="outlined" sx={{mt: 0.5}} label={`не внесено в журнал: ${r.unrecordedHours} ч`}/>
                                                            </Tooltip>
                                                        )}
                                                        {planned && (
                                                            <LinearProgress variant="determinate" value={pct} color={left < 0 ? 'error' : pct >= 100 ? 'success' : 'primary'}
                                                                            sx={{height: 8, borderRadius: 3, my: 1}}/>
                                                        )}
                                                        <Stack direction="row" justifyContent="space-between" sx={{mt: planned ? 0 : 0.5}}>
                                                            <Typography variant="body2">
                                                                <b>{r.hours}</b>{planned ? ` из ${r.totalHours}` : ''} ч
                                                                <Typography component="span" variant="caption" color="text.secondary"> · {r.lessons} пар · {r.source === 'JOURNAL' ? 'по журналу' : 'по расписанию'}</Typography>
                                                            </Typography>
                                                            {planned && (
                                                                <Typography variant="body2" color={left < 0 ? 'error' : 'text.secondary'}>
                                                                    {left >= 0 ? `осталось ${left} ч` : `сверх плана ${-left} ч`}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </Paper>
                                ))}
                            </Box>
                        </>
                    )}
                </Paper>
            )}

            <Dialog open={Boolean(subjectDialog)} onClose={() => !saving && setSubjectDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{subjectDialog?.linking ? 'Связать предмет из расписания' : 'Предмет'}</DialogTitle>
                <DialogContent>
                    {subjectDialog && (
                        <Stack spacing={2} sx={{pt: 1}}>
                            <Typography variant="body2" color="text.secondary">
                                В расписании: «{subjectDialog.row.scheduleSubject}», группа {subjectDialog.row.group}.
                            </Typography>
                            {subjectDialog.linking && (
                                <TextField select label="Предмет группы в системе" value={subjectDialog.tagId}
                                           onChange={(e) => {
                                               const t = subjectDialog.tags.find((x) => x.tagId === e.target.value);
                                               setSubjectDialog({...subjectDialog, tagId: e.target.value});
                                           }}
                                           helperText={subjectDialog.tags.length === 0 ? 'У группы нет предметов — привяжите предмет к группе на её странице' : undefined}>
                                    {subjectDialog.tags.map((t) => <MenuItem key={t.tagId} value={t.tagId}>{t.name}</MenuItem>)}
                                </TextField>
                            )}
                            <TextField label="Ключ в расписании" value={subjectDialog.key}
                                       onChange={(e) => setSubjectDialog({...subjectDialog, key: e.target.value})}
                                       helperText="Пары, в названии предмета которых есть этот текст, относятся к предмету. Пусто — код из названия предмета."/>
                            <Typography variant="caption" color="text.secondary">План часов берётся из журнала АСУ РСО автоматически, вводить его не нужно.</Typography>
                            {dialogError && <Alert severity="error">{dialogError}</Alert>}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSubjectDialog(null)} disabled={saving}>Отмена</Button>
                    <Button variant="contained" onClick={saveSubject} disabled={saving || !subjectDialog?.tagId}>Сохранить</Button>
                </DialogActions>
            </Dialog>
            {loading && <Box display="flex" justifyContent="center" py={4}><CircularProgress/></Box>}
            {!loading && data && days.length === 0 && !error && (
                <Typography color="text.secondary">В этом периоде нет пар.</Typography>
            )}

            {!loading && (
                <ScheduleDays days={days} mode={kind === 'room' ? 'room' : kind === 'group' ? 'group' : 'teacher'}
                              onTeacher={(t) => pick('teacher', t)} onRoom={(r) => pick('room', r)}/>
            )}
        </Container>
    );
};

export default AdminSchedulePage;
